import React, { useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';

export interface IconFilterOption {
  value: string;
  label: string;
}

interface IconFilterDropdownProps {
  /** Mask-image icon class (e.g. `ico--calendar`, `ico--bot`) rendered before the selected label. */
  iconClass: string;
  /** Currently selected option value (matched against `options[].value`). */
  value: string;
  options: IconFilterOption[];
  onChange: (value: string) => void;
}

/**
 * Filter-row dropdown variant used by BotsActivity — adds a leading mask icon next to the
 * selected label via PrimeReact's `valueTemplate`. Extracted so the 4 filters on
 * `BotsActivityPage` stay DRY and the icon-rendering logic stays in one place.
 *
 * The selected-label fallback (`option ?? options.find(...)`) is a defensive guard for the
 * moment when PrimeReact internally resolves the option object from the bare `value` prop —
 * normally it resolves correctly, but the find() keeps the label rendering robust if the
 * value drifts out of `options` for a frame.
 */
const IconFilterDropdown: React.FC<IconFilterDropdownProps> = ({
  iconClass,
  value,
  options,
  onChange,
}) => {
  const fallbackLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? '',
    [options, value]
  );

  return (
    <Dropdown
      value={value}
      onChange={(e) => onChange(e.value as string)}
      options={options}
      optionLabel="label"
      optionValue="value"
      className="dropdown bots-activity__filter-dropdown bots-activity__filter-dropdown--icon"
      valueTemplate={(option: IconFilterOption | undefined) => (
        <span className="bots-activity__filter-value">
          <span
            className={`${iconClass} ico--xs bots-activity__filter-icon`}
            aria-hidden="true"
          />
          <span>{option?.label ?? fallbackLabel}</span>
        </span>
      )}
      itemTemplate={(option: IconFilterOption) => <span>{option.label}</span>}
    />
  );
};

export default IconFilterDropdown;
